import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  UserIcon,
  UsersIcon,
  BuildingOfficeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { useAIMessageModal } from '../hooks/useAIMessageModal'
import { AIMessageModal } from '../components/AIMessageModal'
import { Message } from '../types'
import { ChatbotButton } from '@/components/ChatbotButton'
import { TABS } from '@/constants/tabs'
import { MessageContent } from '../components/MessageContent'
import { usePopup } from '@/contexts/PopupContext'

/**
 * Component cho icon AI
 * Sử dụng để hiển thị các thông báo từ AI
 */
const AIStarsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth="1.5" 
    stroke="currentColor" 
    className={className}
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
    />
  </svg>
)

/**
 * Mock data cho các loại tin nhắn trong hệ thống
 * 
 * Cấu trúc tin nhắn:
 * - id: định danh duy nhất
 * - type: loại tin nhắn (customer/customer_group/chatbot/company)
 * - title: tiêu đề tin nhắn
 * - description: nội dung tóm tắt
 * - time: thời gian gửi/cập nhật
 * - unread: trạng thái chưa đọc
 * - important: đánh dấu tin quan trọng cần xử lý ngay
 * - warning: đánh dấu tin cảnh báo
 * - link: đường dẫn đến chi tiết
 * - icon: biểu tượng hiển thị
 * - iconBg: màu nền của icon
 * - iconColor: màu của icon
 */

export const mockMessages: Message[] = [
  // ===== NHÓM 1: Tin nhắn từ Trợ lý AI (AI gợi ý) =====
  // Các tin từ AI thường có type='chatbot'.
  // Dùng icon và màu sắc khác nhau để phân biệt loại cảnh báo.
  {
    id: 'ai-1',
    type: 'chatbot',
    title: 'Khách sắp rời bỏ❗️',
    description: 'Khách hàng Nguyễn Văn A có dấu hiệu rời bỏ, thực hiện chăm sóc ngay nhé!',
    time: '10 phút',
    unread: true,
    important: true,
    link: '/customers/123',
    icon: AIStarsIcon,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600'
  },
  {
    id: 'ai-2',
    type: 'chatbot',
    title: 'Cơ hội bán hàng ⚡️',
    description: 'Gợi ý cơ hội chốt đơn từ nhóm khách hàng tiềm năng hôm nay 😎',
    time: '30 phút',
    unread: true,
    link: '/opportunities',
    icon: AIStarsIcon,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600'
  },
  {
    id: 'ai-3',
    type: 'chatbot',
    title: 'Chính sách mới cực hot 🔥',
    description: 'Chương trình khuyến mãi mới cần chia sẻ cho 5 khách hàng 🤩',
    time: '1 giờ',
    unread: true,
    link: '/policies',
    icon: AIStarsIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    id: 'ai-4',
    type: 'chatbot',
    title: 'Đơn hàng sắp trễ ‼️',
    description: 'Đơn hàng #DH123 sắp trễ hạn giao hàng, kiểm tra để hỗ trợ ngay nhé!',
    time: '15 phút',
    unread: false,
    important: true,
    link: '/orders/123',
    icon: AIStarsIcon,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600'
  },
  {
    id: 'ai-5',
    type: 'chatbot',
    title: 'Đừng quên hẹn!',
    description: 'Hôm nay bạn có hẹn với khách hàng Lê Văn C lúc 15h tại cửa hàng!',
    time: '2 giờ',
    unread: true,
    link: '/calendar',
    icon: AIStarsIcon,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600'
  },
  {
    id: 'ai-6',
    type: 'chatbot',
    title: '💰 Doanh thu của bạn',
    description: 'Doanh số đã đạt 80% chỉ tiêu tháng, cố gắng đạt 100% nhé! yeah! 💪',
    time: '5 giờ',
    link: '/reports',
    icon: AIStarsIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },

  // ===== NHÓM 2: Tin nhắn từ khách hàng =====
  // Các tin nhắn cá nhân hoặc nhóm khách hàng.
  {
    id: '1',
    type: 'customer',
    title: 'Nguyễn Văn A',
    description: 'Cần tư vấn về sản phẩm mới',
    time: '5 phút',
    unread: true,
    important: true,
    link: '/messages/chat/1',
    icon: UserIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    id: '2',
    type: 'customer',
    title: 'Trần Thị B',
    description: 'Đã xác nhận đơn hàng #456',
    time: '1 giờ',
    link: '/messages/chat/2',
    icon: UserIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    id: 'group1',
    type: 'customer_group',
    title: 'Nhóm khách VIP',
    description: 'Thảo luận về chương trình khuyến mãi mới',
    time: '1 phút',
    unread: true,
    warning: true,
    link: '/messages/group/1',
    icon: UsersIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },

  // ===== NHÓM 3: Tin nhắn nội bộ =====
  // Tin nhắn từ công ty, phòng ban, thông báo nội bộ.
  {
    id: '3',
    type: 'internal',
    title: 'Họp team kinh doanh',
    description: 'Cập nhật KPI Q2/2024 - Tham gia ngay',
    time: '30 phút',
    unread: false,
    important: true,
    icon: BuildingOfficeIcon,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    department: 'business',
    recipients: ['Team Kinh doanh', 'Ban Giám đốc'],
    attachments: ['KPI-Q2-2024.pdf', 'Bao-cao-Q1.xlsx']
  },
  {
    id: '6',
    type: 'internal',
    title: 'Thông báo lịch nghỉ lễ 30/4',
    description: 'Thông báo lịch nghỉ lễ 30/4',
    time: '3 giờ',
    icon: BuildingOfficeIcon,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    department: 'hr',
    recipients: ['Toàn bộ nhân viên'],
    attachments: ['lich-nghi-le.pdf']
  },

  // ===== NHÓM 4: Tin nhắn đã hoàn thành =====
  // Các tin đã xử lý xong sẽ có completed=true.
  {
    id: '9',
    type: 'customer',
    title: 'Lê Thị E',
    description: 'Đã xử lý yêu cầu hoàn tiền',
    time: '5 ngày',
    completed: true,
    link: '/messages/chat/4',
    icon: UserIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    id: '10',
    type: 'chatbot',
    title: 'Nhắc chăm khách mới',
    description: 'Đã gửi thông tin chăm sóc khách hàng mới',
    time: '1 tháng',
    completed: true,
    link: '/customers',
    icon: SparklesIcon,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600'
  }
]

/**
 * Component chính của trang tin nhắn
 *
 * Cấu trúc:
 * 1. Header với các tab lọc tin nhắn
 * 2. Nội dung tin nhắn chia thành các nhóm:
 *    - Đang xử lý: AI gợi ý, Khách hàng, Nội bộ
 *    - Đã hoàn thành
 * 3. Modal hiển thị chi tiết tin nhắn AI
 */
export const MessagesPage: React.FC = () => {
  const [showCompleted, setShowCompleted] = useState(false)
  const location = useLocation()
  
  // Xử lý activeTab từ location state
  const initialTab = (() => {
    const state = location.state as { activeTab?: string } | undefined
    if (state?.activeTab === TABS.AI) {
      return 'chatbot'
    }
    return 'all'
  })()
  
  const [activeTab, setActiveTab] = useState<'all' | 'chatbot' | 'customer' | 'company'>(initialTab)
  const { modalState, handleMessageClick, closeModal } = useAIMessageModal()
  const navigate = useNavigate()
  const { openCount } = usePopup()

  // Hàm chuyển đổi thời gian sang giá trị để so sánh
  const getTimeValue = (time: string): number => {
    const value = parseInt(time.split(' ')[0])
    if (time.includes('phút')) return value
    if (time.includes('giờ')) return value * 60
    if (time.includes('ngày')) return value * 24 * 60
    return 999999
  }

  // Lọc tin nhắn theo tab đã chọn
  const filterMessages = (messages: Message[]) => {
    switch (activeTab) {
      case 'chatbot':
        return messages.filter(msg => msg.type === 'chatbot')
      case 'customer':
        return messages.filter(msg => msg.type === 'customer' || msg.type === 'customer_group')
      case 'company':
        return messages.filter(msg => msg.type === 'internal')
      default:
        return messages
    }
  }

  // ===== XỬ LÝ LỌC VÀ PHÂN NHÓM TIN NHẮN =====
  // Lọc và sắp xếp tin nhắn đang xử lý
  const processingMessages = filterMessages(
    mockMessages
      .filter(msg => !msg.completed)
      .sort((a, b) => getTimeValue(a.time) - getTimeValue(b.time))
  )

  // Lọc và sắp xếp tin nhắn đã hoàn thành
  const completedMessages = filterMessages(
    mockMessages
      .filter(msg => msg.completed)
      .sort((a, b) => getTimeValue(a.time) - getTimeValue(b.time))
  )

  // Xử lý click vào card tin nhắn
  const handleCardClick = (message: Message) => {
    if (message.type === 'chatbot' || message.type === 'internal') {
      handleMessageClick(message)
    } else if (message.link) {
      navigate(message.link)
    }
  }

  const handleCustomerChat = (message: Message) => {
    if (!message.link) return
    
    navigate(message.link, {
      state: {
        customer: {
          id: message.id,
          name: message.title,
          avatar: message.title,
          status: 'Đang hoạt động'
        }
      }
    })
  }

  return (
    <div className="flex flex-col h-screen bg-white overflow-hide">
      {/* ===== HEADER: Thanh filter các nhóm tin nhắn ===== */}
      <div className="sticky top-[56px] z-30 bg-blue-50 shadow-sm">
        <div className="flex items-center px-2 py-2">
          {/* Tab: Tất cả | AI gợi ý | Khách | Nội bộ */}
          <button 
            onClick={() => setActiveTab('all')}
            className={`flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg ${
              activeTab === 'all' ? 'bg-white/80 text-gray-900' : 'text-gray-700'
            }`}>
            <span className="text-sm line-clamp-1">Tất cả</span>
          </button>
          <button 
            onClick={() => setActiveTab('chatbot')}
            className={`flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg ${
              activeTab === 'chatbot' ? 'bg-gradient-to-r from-blue-500 to-green-600 text-white' : 'text-gray-700'
            }`}>
            <span className="text-sm line-clamp-1">AI gợi ý</span>
          </button>
          <button 
            onClick={() => setActiveTab('customer')}
            className={`flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg ${
              activeTab === 'customer' ? 'bg-blue-500 text-white' : 'text-gray-700'
            }`}>
            <span className="text-sm line-clamp-1">Khách</span>
          </button>
          <button 
            onClick={() => setActiveTab('company')}
            className={`flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 rounded-lg ${
              activeTab === 'company' ? 'bg-blue-500 text-white' : 'text-gray-700'
            }`}>
            <span className="text-sm">Nội bộ</span>
          </button>
        </div>
      </div>

      {/* ===== NỘI DUNG: Hiển thị các nhóm tin nhắn ===== */}
      {/* Component MessageContent sẽ tự động chia nhóm theo props truyền vào */}
      <MessageContent
        processingMessages={processingMessages}
        completedMessages={completedMessages}
        showCompleted={showCompleted}
        setShowCompleted={setShowCompleted}
        handleCustomerChat={handleCustomerChat}
        handleCardClick={handleCardClick}
      />

      {/* ===== MODAL: Chi tiết tin nhắn AI, nội bộ... ===== */}
      <AIMessageModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        type={modalState.type}
        message={modalState.message}
      />
    </div>
  )
}

export default MessagesPage 